/**
 * <DeploymentEditor> — Engineer-tier editor for the four most-frequently-
 * tweaked Deployment fields (#250): image / replicas / env / resources.
 *
 * Shape matches `WorkloadDrawer`: fixed right-anchored panel, X to close.
 *
 * Out of scope (locked behind read-only sections / future issues):
 *   - StatefulSet / DaemonSet editing (separate sub-issue)
 *   - env `valueFrom` (Secret/ConfigMap refs) — read-only in v1; #251 owns
 *     the secret-rotation flow
 *   - Volumes / probes / affinity / nodeSelector
 *   - Multi-container editing — container[0] only; the rest are listed
 *     read-only so the engineer knows what they're NOT editing
 *
 * Diff preview: this codebase has no `diff` dep, so we render a simple
 * before/after table on the changed fields. Engineers eyeball the rows
 * before clicking Apply.
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { clusterApi } from '@/api/client';
import type {
  DeploymentEditableSpec,
  DeploymentEditablePatch,
  DeploymentEnvVar,
  DeploymentResources,
} from '@/types';

interface Props {
  siteId: string;
  namespace: string;
  name: string;
  onClose: () => void;
}

interface FormState {
  image: string;
  replicas: number;
  env: DeploymentEnvVar[];
  resources: {
    requests: { cpu: string; memory: string };
    limits: { cpu: string; memory: string };
  };
}

function specToForm(spec: DeploymentEditableSpec): FormState {
  return {
    image: spec.image,
    replicas: spec.replicas,
    env: spec.env.map((e) => ({ ...e })),
    resources: {
      requests: {
        cpu: spec.resources.requests?.cpu ?? '',
        memory: spec.resources.requests?.memory ?? '',
      },
      limits: {
        cpu: spec.resources.limits?.cpu ?? '',
        memory: spec.resources.limits?.memory ?? '',
      },
    },
  };
}

function formResourcesToWire(r: FormState['resources']): DeploymentResources {
  const out: DeploymentResources = {};
  const requests: Record<string, string> = {};
  if (r.requests.cpu) requests.cpu = r.requests.cpu;
  if (r.requests.memory) requests.memory = r.requests.memory;
  if (Object.keys(requests).length > 0) out.requests = requests;

  const limits: Record<string, string> = {};
  if (r.limits.cpu) limits.cpu = r.limits.cpu;
  if (r.limits.memory) limits.memory = r.limits.memory;
  if (Object.keys(limits).length > 0) out.limits = limits;

  return out;
}

/** Compute the patch + diff rows. Field is only "changed" when the operator
 *  actually edits it — env list is compared by canonical name/value pairs. */
export interface DiffRow {
  field: string;
  before: string;
  after: string;
}

export function computeDiff(
  spec: DeploymentEditableSpec,
  form: FormState,
): { patch: DeploymentEditablePatch; rows: DiffRow[] } {
  const rows: DiffRow[] = [];
  const patch: DeploymentEditablePatch = {};

  if (form.image !== spec.image) {
    rows.push({ field: 'image', before: spec.image, after: form.image });
    patch.image = form.image;
  }
  if (form.replicas !== spec.replicas) {
    rows.push({ field: 'replicas', before: String(spec.replicas), after: String(form.replicas) });
    patch.replicas = form.replicas;
  }

  // env: deep equality on a sorted/canonicalised representation
  const beforeEnv = JSON.stringify([...spec.env].sort((a, b) => a.name.localeCompare(b.name)));
  const filteredFormEnv = form.env.filter((e) => e.name.length > 0);
  const afterEnv = JSON.stringify(
    [...filteredFormEnv].sort((a, b) => a.name.localeCompare(b.name)),
  );
  if (beforeEnv !== afterEnv) {
    rows.push({
      field: 'env',
      before: spec.env.map((e) => `${e.name}=${e.value}`).join('\n') || '(none)',
      after: filteredFormEnv.map((e) => `${e.name}=${e.value}`).join('\n') || '(none)',
    });
    patch.env = filteredFormEnv;
  }

  const beforeResources = JSON.stringify(spec.resources ?? {});
  const wireResources = formResourcesToWire(form.resources);
  const afterResources = JSON.stringify(wireResources);
  if (beforeResources !== afterResources) {
    rows.push({
      field: 'resources',
      before: beforeResources,
      after: afterResources,
    });
    patch.resources = wireResources;
  }

  return { patch, rows };
}

export default function DeploymentEditor({ siteId, namespace, name, onClose }: Props) {
  const [spec, setSpec] = useState<DeploymentEditableSpec | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    clusterApi
      .getDeploymentSpec(siteId, namespace, name)
      .then((s) => {
        if (cancelled) return;
        setSpec(s);
        setForm(specToForm(s));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, namespace, name]);

  const diff = useMemo(() => {
    if (!spec || !form) return { patch: {} as DeploymentEditablePatch, rows: [] };
    return computeDiff(spec, form);
  }, [spec, form]);

  const hasChanges = diff.rows.length > 0;

  const handleApply = async () => {
    if (!hasChanges || !form) return;
    setApplying(true);
    try {
      await clusterApi.patchDeployment(siteId, namespace, name, diff.patch);
      // Best-effort toast; the codebase doesn't have a toast lib wired yet,
      // and WorkloadCard's restart action also uses alert() (#236 convention).
      window.alert(`Updated ${namespace}/${name}: ${diff.rows.map((r) => r.field).join(', ')}`);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      window.alert(`Failed to update ${namespace}/${name}: ${msg}`);
      setApplying(false);
    }
  };

  return (
    <div
      data-testid="deployment-editor"
      className="fixed inset-y-0 right-0 z-50 w-[720px] max-w-full bg-surface border-l border-surface-elevated shadow-2xl flex flex-col"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-surface-elevated">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-medium text-silver-bright truncate">Edit Deployment</h3>
          <p className="text-xs text-silver-muted mt-0.5 font-mono truncate">
            {namespace}/{name}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-silver-muted hover:text-silver-bright shrink-0"
          aria-label="Close deployment editor"
          data-testid="deployment-editor-close"
        >
          <X size={16} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div data-testid="deployment-editor-loading" className="p-4 space-y-2 animate-pulse">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-10 bg-surface-elevated rounded" />
            ))}
          </div>
        )}

        {error && (
          <div data-testid="deployment-editor-error" className="p-4 text-xs text-status-error">
            {error}
          </div>
        )}

        {spec && form && !loading && !error && (
          <div className="p-4 space-y-5">
            {/* Image */}
            <div>
              <label className="block text-xs font-medium text-silver-bright mb-1" htmlFor="dep-edit-image">
                Image
              </label>
              <input
                id="dep-edit-image"
                data-testid="deployment-editor-image"
                type="text"
                value={form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
                className="w-full bg-surface-elevated border border-surface-elevated rounded px-2 py-1.5 text-xs text-silver-bright font-mono focus:outline-none focus:border-gold/60"
              />
            </div>

            {/* Replicas */}
            <div>
              <label className="block text-xs font-medium text-silver-bright mb-1" htmlFor="dep-edit-replicas">
                Replicas
              </label>
              <input
                id="dep-edit-replicas"
                data-testid="deployment-editor-replicas"
                type="number"
                min={0}
                max={50}
                value={form.replicas}
                onChange={(e) =>
                  setForm({ ...form, replicas: Number.parseInt(e.target.value, 10) || 0 })
                }
                className="w-32 bg-surface-elevated border border-surface-elevated rounded px-2 py-1.5 text-xs text-silver-bright font-mono focus:outline-none focus:border-gold/60"
              />
            </div>

            {/* Env */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-silver-bright">
                  Environment Variables
                </label>
                <button
                  type="button"
                  data-testid="deployment-editor-env-add"
                  onClick={() => setForm({ ...form, env: [...form.env, { name: '', value: '' }] })}
                  className="text-xs text-gold hover:text-gold/80 inline-flex items-center gap-1"
                >
                  <Plus size={12} /> Add
                </button>
              </div>
              <div className="space-y-1.5">
                {form.env.length === 0 && (
                  <div className="text-xs text-silver-muted italic">No env vars set.</div>
                )}
                {form.env.map((e, i) => (
                  <div key={i} className="flex items-center gap-1.5" data-testid={`deployment-editor-env-row-${i}`}>
                    <input
                      type="text"
                      placeholder="NAME"
                      value={e.name}
                      data-testid={`deployment-editor-env-name-${i}`}
                      onChange={(ev) => {
                        const next = [...form.env];
                        next[i] = { ...next[i]!, name: ev.target.value };
                        setForm({ ...form, env: next });
                      }}
                      className="flex-1 bg-surface-elevated border border-surface-elevated rounded px-2 py-1 text-xs text-silver-bright font-mono focus:outline-none focus:border-gold/60"
                    />
                    <input
                      type="text"
                      placeholder="value"
                      value={e.value}
                      data-testid={`deployment-editor-env-value-${i}`}
                      onChange={(ev) => {
                        const next = [...form.env];
                        next[i] = { ...next[i]!, value: ev.target.value };
                        setForm({ ...form, env: next });
                      }}
                      className="flex-1 bg-surface-elevated border border-surface-elevated rounded px-2 py-1 text-xs text-silver-bright font-mono focus:outline-none focus:border-gold/60"
                    />
                    <button
                      type="button"
                      data-testid={`deployment-editor-env-remove-${i}`}
                      onClick={() => {
                        const next = [...form.env];
                        next.splice(i, 1);
                        setForm({ ...form, env: next });
                      }}
                      className="text-silver-muted hover:text-status-error"
                      aria-label="Remove env var"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Resources */}
            <div>
              <label className="block text-xs font-medium text-silver-bright mb-1">Resources</label>
              <div className="grid grid-cols-2 gap-2">
                {(['requests', 'limits'] as const).map((kind) => (
                  <div key={kind} className="bg-surface-elevated/30 rounded p-2 space-y-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-silver-muted">{kind}</div>
                    {(['cpu', 'memory'] as const).map((field) => (
                      <div key={field}>
                        <label
                          className="block text-[10px] text-silver-muted mb-0.5"
                          htmlFor={`dep-edit-${kind}-${field}`}
                        >
                          {field}
                        </label>
                        <input
                          id={`dep-edit-${kind}-${field}`}
                          data-testid={`deployment-editor-${kind}-${field}`}
                          type="text"
                          placeholder={field === 'cpu' ? '100m' : '256Mi'}
                          value={form.resources[kind][field]}
                          onChange={(ev) =>
                            setForm({
                              ...form,
                              resources: {
                                ...form.resources,
                                [kind]: { ...form.resources[kind], [field]: ev.target.value },
                              },
                            })
                          }
                          className="w-full bg-surface-elevated border border-surface-elevated rounded px-2 py-1 text-xs text-silver-bright font-mono focus:outline-none focus:border-gold/60"
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Read-only: extra containers */}
            {spec.containers.length > 1 && (
              <div
                data-testid="deployment-editor-extra-containers"
                className="border border-amber-700/30 bg-amber-900/10 rounded p-3"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle size={14} className="text-status-warning shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-status-warning mb-1">
                      Multi-container Deployment ({spec.containers.length} containers)
                    </div>
                    <p className="text-[11px] text-silver-muted mb-2">
                      Editor edits container <span className="font-mono">{spec.containers[0]?.name}</span> only.
                      Other containers are read-only here:
                    </p>
                    <ul className="text-[11px] text-silver-muted font-mono space-y-0.5">
                      {spec.containers.slice(1).map((c) => (
                        <li key={c.name}>
                          {c.name}: {c.image}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Read-only: envFrom */}
            {spec.envFrom && spec.envFrom.length > 0 && (
              <div data-testid="deployment-editor-env-from" className="border border-surface-elevated rounded p-3">
                <div className="text-xs font-medium text-silver-bright mb-1">
                  envFrom (read-only)
                </div>
                <p className="text-[11px] text-silver-muted mb-2">
                  Secret/ConfigMap refs are not editable in this UI. Use the secret rotation flow.
                </p>
                <ul className="text-[11px] text-silver-muted font-mono space-y-0.5">
                  {spec.envFrom.map((ref, i) => (
                    <li key={i}>
                      {ref.secretRef ? `secretRef: ${ref.secretRef}` : null}
                      {ref.configMapRef ? `configMapRef: ${ref.configMapRef}` : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Diff preview */}
            {showDiff && (
              <div
                data-testid="deployment-editor-diff"
                className="border border-gold/30 rounded p-3 bg-surface-elevated/40"
              >
                <div className="text-xs font-medium text-gold mb-2">Diff preview</div>
                {diff.rows.length === 0 ? (
                  <div className="text-xs text-silver-muted italic">No changes.</div>
                ) : (
                  <div className="space-y-2">
                    {diff.rows.map((row) => (
                      <div
                        key={row.field}
                        data-testid={`deployment-editor-diff-row-${row.field}`}
                        className="text-[11px]"
                      >
                        <div className="text-silver-bright font-medium mb-0.5">{row.field}</div>
                        <pre className="text-status-error/80 bg-status-error/5 p-1.5 rounded whitespace-pre-wrap font-mono">- {row.before}</pre>
                        <pre className="text-status-healthy/90 bg-status-healthy/5 p-1.5 rounded whitespace-pre-wrap font-mono mt-0.5">+ {row.after}</pre>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-surface-elevated">
        <button
          type="button"
          onClick={onClose}
          className="text-xs px-3 py-1.5 rounded border border-surface-elevated text-silver-muted hover:text-silver-bright hover:border-silver-muted/40 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="deployment-editor-diff-toggle"
          disabled={!hasChanges}
          onClick={() => setShowDiff((v) => !v)}
          className="text-xs px-3 py-1.5 rounded border border-gold/30 text-gold hover:border-gold/60 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {showDiff ? 'Hide diff' : 'Preview diff'}
        </button>
        <button
          type="button"
          data-testid="deployment-editor-apply"
          disabled={!hasChanges || applying}
          onClick={handleApply}
          className="text-xs px-3 py-1.5 rounded bg-gold/10 border border-gold/40 text-gold hover:bg-gold/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {applying ? 'Applying…' : 'Apply'}
        </button>
      </div>
    </div>
  );
}
